import React, { useContext, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '~/components/Dialog'
import { Nullable } from '~/types'
import { FeedPageContext } from '../..'
import notify from '~/components/Notify'
import { Form, Icon, Popup } from 'semantic-ui-react'
import CustomInput from '~/components/CustomInput'
import WarningIcon from '../WarningIcon'
import { validateBarleyFields } from '../../utils'

export interface BarleyDataType {
  index: number
  key: string
  feedName: string
  dryMatterLimitMinValue: number
  dryMatterLimitMaxValue: number
  energyLimitMinValue: number
  energyLimitMaxValue: number
  rawAshLimitMinValue: number
  rawAshLimitMaxValue: number
  rawAshWarningMinValue: number
  rawAshWarningMaxValue: number
  rawProteinLimitMinValue: number
  rawProteinLimitMaxValue: number
  rawProteinWarningMinValue: number
  rawProteinWarningMaxValue: number
  rawFatLimitMinValue: number
  rawFatLimitMaxValue: number
  rawFatWarningMinValue: number
  rawFatWarningMaxValue: number
  rawFiberLimitMinValue: number
  rawFiberLimitMaxValue: number
  rawFiberWarningMinValue: number
  rawFiberWarningMaxValue: number
  dryMatter: string | number
  energy: string | number
  rawAsh: string | number
  rawProtein: string | number
  rawFat: string | number
  rawFiber: string | number
  isJuicyFeed: boolean
}

interface Props {
  barleyData: Nullable<BarleyDataType>
  handleClose: (data?) => void
}

export interface BarleyFormValues {
  dryMatter: string
  energy: string
  rawAsh: string
  rawProtein: string
  rawFat: string
  rawFiber: string
}

interface FormErrors {
  dryMatter: string | null
  energy: string | null
  rawAsh: string | null
  rawProtein: string | null
  rawFat: string | null
  rawFiber: string | null
}

const BarleyModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()

  const { setEnergyCalcData } = useContext(FeedPageContext)

  const { barleyData, handleClose } = props

  const [values, setValues] = useState<BarleyFormValues>({
    dryMatter: '',
    energy: '',
    rawAsh: '',
    rawProtein: '',
    rawFat: '',
    rawFiber: ''
  })

  const [errors, setErrors] = useState<FormErrors>({
    dryMatter: null,
    energy: null,
    rawAsh: null,
    rawProtein: null,
    rawFat: null,
    rawFiber: null
  })

  const parseValue = (value: string | number): number => {
    if (value === '' || value === null || value === undefined) return NaN
    return typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value
  }

  const isInWarningRange = (value: string | number, minWarning: number, maxWarning: number): boolean => {
    const numValue = parseValue(value)
    if (isNaN(numValue)) return false
    return numValue < minWarning || numValue > maxWarning
  }

  const handleFieldChange = (name: keyof BarleyFormValues, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
    const error = validateBarleyFields(name, value, barleyData)
    setErrors(prev => ({ ...prev, [name]: error }))
  }

  useEffect(() => {
    if (barleyData) {
      const newValues = {
        dryMatter: barleyData?.dryMatter?.toString() || '',
        energy: barleyData?.energy?.toString() || '',
        rawAsh: barleyData?.rawAsh?.toString() || '',
        rawProtein: barleyData?.rawProtein?.toString() || '',
        rawFat: barleyData?.rawFat?.toString() || '',
        rawFiber: barleyData?.rawFiber?.toString() || ''
      }
      setValues(newValues)
      setErrors({
        dryMatter: null,
        energy: null,
        rawAsh: null,
        rawProtein: null,
        rawFat: null,
        rawFiber: null
      })
    }
  }, [barleyData])

  function resetValues() {
    setValues({
      dryMatter: '',
      energy: '',
      rawAsh: '',
      rawProtein: '',
      rawFat: '',
      rawFiber: ''
    })
    setErrors({
      dryMatter: null,
      energy: null,
      rawAsh: null,
      rawProtein: null,
      rawFat: null,
      rawFiber: null
    })
  }

  const isFormValid = useMemo(() => {
    const { isJuicyFeed } = barleyData || {}

    const requiredFields: (keyof BarleyFormValues)[] = isJuicyFeed
      ? ['dryMatter', 'energy']
      : ['dryMatter', 'energy', 'rawAsh', 'rawProtein', 'rawFat', 'rawFiber']

    for (const field of requiredFields) {
      if (!values[field] || values[field] === '') {
        return false
      }
      const error = validateBarleyFields(field, values[field], barleyData)
      if (error) {
        return false
      }
    }
    return true
  }, [values, barleyData])

  function onSubmit() {
    const { isJuicyFeed } = barleyData || {}

    const payloadBarley = isJuicyFeed ?
      { dryMatter: values.dryMatter, energy: values.energy } :
      { ...values }

    resetValues()
    notify(t('The content values for {{feedName}} have been successfully adjusted.', { feedName: barleyData?.feedName || '' }), 'success')
    setEnergyCalcData(null)
    handleClose(payloadBarley)
  }

  const renderErrorIcon = (fieldName: keyof FormErrors) => {
    const error = errors[fieldName]
    if (error) {
      return <Popup
        key={fieldName}
        content={error}
        trigger={<Icon name='warning sign' color='red' style={{ fontSize: '1.2em', marginLeft: 5 }} />}
      />
    }
    return null
  }

  function renderBarleyValues() {
    const { isJuicyFeed } = barleyData || {}

    return <Form>
      <Form.Group style={{ gap: 20, marginBottom: isJuicyFeed ? 0 : '1em' }}>
        <CustomInput
          value={values.dryMatter}
          onChange={(value) => handleFieldChange('dryMatter', String(value || ''))}
          error={Boolean(errors.dryMatter)}
          label={<label>
            <label>{t('TM')}</label>
            {renderErrorIcon('dryMatter')}
          </label>}
          placeholder={t('TM')}
          decimalScale={2}
          suffix={' % TM'}
          withLabels
          allowDecimals={true}
        />
        <CustomInput
          value={values.energy}
          onChange={(value) => handleFieldChange('energy', String(value || ''))}
          error={Boolean(errors.energy)}
          label={<label>
            <label>{t('Energy')}</label>
            {renderErrorIcon('energy')}
          </label>}
          decimalScale={2}
          withLabels
          suffix={' MJ NEL/kg TM'}
          allowDecimals={true}
          placeholder={t('Energy')}
        />
        {!isJuicyFeed && <CustomInput
          value={values.rawAsh}
          onChange={(value) => handleFieldChange('rawAsh', String(value || ''))}
          error={Boolean(errors.rawAsh)}
          label={<label>
            <label>{'Rohasche'}</label>
            {isInWarningRange(values.rawAsh, barleyData?.rawAshWarningMinValue || 0, barleyData?.rawAshWarningMaxValue || 999) && <WarningIcon
              content={`Der Rohaschewert liegt außerhalb des Normalbereichs von ${barleyData?.rawAshWarningMinValue} bis ${barleyData?.rawAshWarningMaxValue} g/kg TM.`}
              style={{ marginLeft: 5 }}
            />}
            {renderErrorIcon('rawAsh')}
          </label>}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
        />}
      </Form.Group>
      {!isJuicyFeed && <Form.Group style={{ gap: 20 }}>
        <CustomInput
          value={values.rawProtein}
          onChange={(value) => handleFieldChange('rawProtein', String(value || ''))}
          error={Boolean(errors.rawProtein)}
          label={<label>
            <label>{'Rohprotein'}</label>
            {isInWarningRange(values.rawProtein, barleyData?.rawProteinWarningMinValue || 0, barleyData?.rawProteinWarningMaxValue || 999) && <WarningIcon
              content={`Der Rohproteinwert liegt außerhalb des Normalbereichs von ${barleyData?.rawProteinWarningMinValue} bis ${barleyData?.rawProteinWarningMaxValue} g/kg TM.`}
              style={{ marginLeft: 5 }}
            />}
            {renderErrorIcon('rawProtein')}
          </label>}
          placeholder={'in g/kg TM'}
          decimalScale={2}
          suffix={' g/kg TM'}
          withLabels
          allowDecimals={true}
        />
        <CustomInput
          value={values.rawFat}
          onChange={(value) => handleFieldChange('rawFat', String(value || ''))}
          error={Boolean(errors.rawFat)}
          label={<label>
            <label>{'Rohfett'}</label>
            {isInWarningRange(values.rawFat, barleyData?.rawFatWarningMinValue || 0, barleyData?.rawFatWarningMaxValue || 999) && <WarningIcon
              content={`Der Rohfettwert liegt außerhalb des Normalbereichs von ${barleyData?.rawFatWarningMinValue} bis ${barleyData?.rawFatWarningMaxValue} g/kg TM.`}
              style={{ marginLeft: 5 }}
            />}
            {renderErrorIcon('rawFat')}
          </label>}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
        />
        <CustomInput
          value={values.rawFiber}
          onChange={(value) => handleFieldChange('rawFiber', String(value || ''))}
          error={Boolean(errors.rawFiber)}
          label={<label>
            <label>{'Rohfaser'}</label>
            {isInWarningRange(values.rawFiber, barleyData?.rawFiberWarningMinValue || 0, barleyData?.rawFiberWarningMaxValue || 999) && <WarningIcon
              content={`Der Rohfaserwert liegt außerhalb des Normalbereichs von ${barleyData?.rawFiberWarningMinValue} bis ${barleyData?.rawFiberWarningMaxValue} g/kg TM.`}
              style={{ marginLeft: 5 }}
            />}
            {renderErrorIcon('rawFiber')}
          </label>}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
        />
      </Form.Group>}
    </Form>
  }

  const { isJuicyFeed } = barleyData || {}

  return <Dialog
    textHeader={t('Edit barley for {{feedName}}', { feedName: barleyData?.feedName || '' })}
    textClose={t('Cancel')}
    style={{ width: isJuicyFeed ? 550 : 700 }}
    textSubmit={t('Adjust')}
    isDialogOpen={Boolean(barleyData)}
    disableSubmit={!isFormValid}
    handleSubmit={onSubmit}
    handleClose={() => {
      resetValues()
      handleClose()
    }}
    textContent={renderBarleyValues()}
  />
}
export default BarleyModal
